/**
 * The e-scooter: a KuKirin G2, its model, its physics and its rider.
 *
 * **The machine** is a 48 V, 800 W off-road kick scooter at real scale: 10-inch
 * knobbly pneumatic tyres 0.97 m apart, a 17 cm deck with the battery in it,
 * a folding stem up to bars at 1.19 m, a twin-leg sprung fork at the front
 * and a swingarm on two coil shocks at the rear, disc brakes at both ends,
 * a round display on the bars, a headlamp on the stem and LED strips along
 * the deck's edges that glow at night. The frame is matt black; the trim —
 * fork guards, shock springs, deck stripe, rim rings — takes the paint, so a
 * parked one is red or blue or white as the instance says.
 *
 * **The physics** is the motorbike's (`stepMotorbike`), with the scooter's
 * own numbers (`SCOOTER_SPEC`): a brisk pull to about 45 km/h, small wheels
 * that lean less and grip less, a few centimetres of travel, and no
 * wheelies: a hub motor on a 24 kg frame lifts nothing. It rolls off a kerb
 * rather than flying off a jump, and falls at full gravity when it does.
 *
 * **The rider** stands on the deck, one foot ahead of the other, knees soft,
 * hands on the bars, crouching a little as the speed comes on and leaning
 * with the machine in a turn; stopped, the left foot goes down to the ground.
 */

import {
  BoxGeometry,
  CatmullRomCurve3,
  CircleGeometry,
  Color,
  CylinderGeometry,
  ExtrudeGeometry,
  Group,
  LatheGeometry,
  Matrix4,
  Mesh,
  Path,
  Shape,
  SpotLight,
  TubeGeometry,
  Vector2,
  Vector3,
} from 'three';
import type { BufferGeometry, MeshStandardMaterial } from 'three';
import type { Surface } from './Vehicle.js';
import type { RiderPose } from './character/crowd.js';
import type { TwoWheelerSpec, RiderMotion, RiderState } from './Motorbike.js';
import {
  Kit, alu, beam, black, chrome, darkAlu, faceted, placed, rimBlack, rod, rubber, slab, smoothstep, steel, tube,
} from './Motorbike.js';
import type { V3 } from './Motorbike.js';

// --- geometry -------------------------------------------------------------------
// The scooter's frame: x right, y up, z back (forward is -Z), origin on the
// ground midway between the axles, at static sag.

/** Steering axis tilt from vertical: 12°. */
const RAKE = 0.21;
/** Where the steering axis passes at the height of the bar clamp. */
const HEAD = new Vector3(0, 1.0, -0.308);
/** Head to the front axle along the stem, at sag. */
const FORK_AT_SAG = 0.891;
/** Swingarm pivot under the deck's tail, and the rear axle from it at sag. */
const PIVOT = new Vector3(0, 0.2, 0.3);
const ARM = new Vector3(0, -0.073, 0.185);
const ARM_LENGTH = Math.hypot(ARM.y, ARM.z);
/** The rear shocks: their tops on the frame, their feet on the swingarm (from the pivot), one each side. */
const SHOCK_TOP = new Vector3(0.075, 0.345, 0.34);
const SHOCK_FOOT = new Vector3(0.075, 0.0, 0.14);
/** A grip in the steering's tilted frame; the right one, the left mirrored. */
const GRIP = new Vector3(0.27, 0.19, -0.02);
/** The deck: its top, its ends, its width. */
const DECK_TOP = 0.17;
const DECK_FRONT = -0.3;
const DECK_BACK = 0.36;
const DECK_WIDTH = 0.21;

export const SCOOTER = {
  kind: 'scooter',
  wheelbase: 0.97,
  rake: RAKE,
  frontAxleZ: HEAD.z - Math.sin(RAKE) * FORK_AT_SAG,
  rearAxleZ: PIVOT.z + ARM.z,
  frontRadius: 0.127,
  rearRadius: 0.127,
  frontTyre: 0.065,
  rearTyre: 0.065,
  /** Travel and sag, metres: short coil springs at both ends. */
  forkTravel: 0.065,
  rearTravel: 0.06,
  frontSag: 0.015,
  rearSag: 0.015,
  /** Standing on the deck: eyes at 1.7 m. */
  eyeHeight: 1.72,
} as const;

/**
 * How it rides. A G2's 800 W hub motor pulls a rider off the line at about
 * 2.6 m/s² and tails off toward its top of a little under 50 km/h; the
 * brakes are two small discs and the rider's weight is high, so it stops
 * well but not like the bike. Small wheels lean less far and let go sooner.
 */
export const SCOOTER_RIDE = {
  power: 2.6,
  brake: 6.5,
  /** Walked backwards a step or two. */
  reverseMax: 0.8,
  maxSpeed: 13.3,
  drag: 0.0012,
  rolling: 0.18,
  /** Off the throttle a hub motor regenerates a little: a gentle drag. */
  engineBrake: 0.7,
  steerLock: 0.75,
  cornerGrip: 7.5,
  maxLean: 0.62,
  leanRate: 11,
  lateralGrip: 9,
  slideGrip: 1.8,
  slideBrake: 2.6,
  parkingHold: 2,
  gravity: 9.81,
} as const;

/** Tarmac, and off it: the knobblies cope with gravel and grass, slower. */
export const SCOOTER_ROAD: Surface = { grip: 1, rolling: SCOOTER_RIDE.rolling, topSpeed: 1 };
export const SCOOTER_DIRT: Surface = { grip: 0.75, rolling: 0.9, topSpeed: 0.8 };

export const SCOOTER_SPEC: TwoWheelerSpec = {
  geometry: SCOOTER, ride: SCOOTER_RIDE, road: SCOOTER_ROAD, dirt: SCOOTER_DIRT,
  airGravity: 1, radius: 0.3, circleOffset: 0.35, strikeHeight: 0.45, heave: 17, pitchRate: 18,
};

// --- materials ------------------------------------------------------------------

const frameBlack = faceted(0x1a1b1e, 0.5, 0.3);
const gripTape = faceted(0x111214, 0.98);
/** The trim's colour: a parked scooter takes its own, and so does the one ridden from it. */
export const scooterPaint = faceted(0xc8262c, 0.4, 0.1);
/** The display's glass, dark until the scooter is switched on: one per model, so a parked one stays dark. */
const makeDisplay = () => faceted(0x0b0c10, 0.2, 0.2, { emissive: new Color(0x3aa0ff), emissiveIntensity: 0 });
/** The lamps, exported so the traffic could find them as it does the bike's. */
export const scooterHeadlamp = faceted(0xfff4df, 0.2, 0.1, { emissive: new Color(0xfff0c8), emissiveIntensity: 0.4 });
export const scooterTaillamp = faceted(0xb3161b, 0.3, 0.1, { emissive: new Color(0xff2a1a), emissiveIntensity: 0.5 });
/** The LED strips along the deck: they glow in the paint's colour while ridden at night. One per model, as the display. */
const makeDeckLeds = () => faceted(0x3a3a3e, 0.3, 0, { emissive: new Color(0xc8262c), emissiveIntensity: 0 });

const X_AXIS = new Vector3(1, 0, 0);
const Y_AXIS = new Vector3(0, 1, 0);
const DOWN = new Vector3(0, -1, 0);
const v4 = new Vector3();
const vec = (p: V3) => new Vector3(p[0], p[1], p[2]);

// --- building blocks ------------------------------------------------------------

/**
 * A 10-inch wheel: a knobbly tyre, a five-spoke alloy rim with a ring of
 * trim, the hub motor's fat drum on the rear, and a small disc. Spins about X.
 */
function buildWheel(r: number, w: number, motor: boolean): Group {
  const kit = new Kit();
  const knob = 0.011;
  const c = r - knob;
  const rimR = 0.07;
  const section = [
    new Vector2(rimR, -w * 0.42), new Vector2(c - 0.02, -w / 2), new Vector2(c - 0.006, -w * 0.4),
    new Vector2(c, -w * 0.22), new Vector2(c, w * 0.22), new Vector2(c - 0.006, w * 0.4),
    new Vector2(c - 0.02, w / 2), new Vector2(rimR, w * 0.42),
  ];
  kit.add(rubber, new LatheGeometry(section, 22).rotateZ(Math.PI / 2));
  // Blocks in a staggered row down the middle and canted ones on the shoulders.
  const n = Math.round((Math.PI * 2 * c) / 0.04);
  const knobs = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    knobs.push(new BoxGeometry(w * 0.34, knob, 0.02).translate((i % 2 === 0 ? 1 : -1) * w * 0.12, c + knob / 2, 0).rotateX(a));
    for (const s of [-1, 1]) {
      knobs.push(new BoxGeometry(w * 0.2, knob * 0.9, 0.018).rotateZ(-s * 0.6).translate(s * w * 0.4, c - 0.006 + knob / 2, 0).rotateX(a + Math.PI / n));
    }
  }
  kit.add(rubber, ...knobs);
  if (motor) {
    // The hub motor: a drum nearly the rim's size, with cooling ribs.
    kit.add(frameBlack, new CylinderGeometry(rimR - 0.004, rimR - 0.004, w * 0.78, 18).rotateZ(Math.PI / 2));
    for (const s of [-1, 1]) {
      kit.add(darkAlu, new CylinderGeometry(rimR - 0.012, rimR - 0.012, 0.006, 18).rotateZ(Math.PI / 2).translate(s * w * 0.39, 0, 0));
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        kit.add(darkAlu, new BoxGeometry(0.006, 0.01, 0.028).translate(s * w * 0.4, 0.045 * Math.cos(a), 0.045 * Math.sin(a)).rotateX(a));
      }
    }
  } else {
    // A five-spoke alloy with a hub.
    kit.add(rimBlack, new CylinderGeometry(rimR, rimR, w * 0.8, 18, 1, true).rotateZ(Math.PI / 2));
    kit.add(rimBlack, new CylinderGeometry(0.03, 0.03, w * 0.6, 10).rotateZ(Math.PI / 2));
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      kit.add(rimBlack, beam([0, 0.02 * Math.cos(a), 0.02 * Math.sin(a)], [0, (rimR - 0.004) * Math.cos(a), (rimR - 0.004) * Math.sin(a)], 0.02, 0.016));
    }
  }
  // A ring of the trim colour on the rim's lip, both sides.
  for (const s of [-1, 1]) kit.add(scooterPaint, new CylinderGeometry(rimR + 0.004, rimR + 0.004, 0.005, 18, 1, true).rotateZ(Math.PI / 2).translate(s * w * 0.41, 0, 0));
  // The axle, and a 120 mm disc on the left with its carrier.
  kit.add(chrome, new CylinderGeometry(0.008, 0.008, w + 0.06, 6).rotateZ(Math.PI / 2));
  const disc = new Shape().absarc(0, 0, 0.06, 0, Math.PI * 2, false);
  disc.holes.push(new Path().absarc(0, 0, 0.04, 0, Math.PI * 2, true));
  for (let i = 0; i < 6; i++) {
    const a = ((i + 0.5) / 6) * Math.PI * 2;
    disc.holes.push(new Path().absarc(0.05 * Math.cos(a), 0.05 * Math.sin(a), 0.004, 0, Math.PI * 2, true));
  }
  kit.add(steel, new ExtrudeGeometry(disc, { depth: 0.003, bevelEnabled: false, curveSegments: 4 }).rotateY(Math.PI / 2).translate(-w / 2 - 0.012, 0, 0));
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    kit.add(darkAlu, beam([-w / 2 - 0.011, 0.012 * Math.cos(a), 0.012 * Math.sin(a)], [-w / 2 - 0.011, 0.042 * Math.cos(a), 0.042 * Math.sin(a)], 0.006, 0.01));
  }
  return kit.into(new Group());
}

/** The deck's outline in plan (z, x), a long board rounded at both ends. */
function deckShape(inset = 0): Shape {
  const hw = DECK_WIDTH / 2 - inset, f = DECK_FRONT + inset, b = DECK_BACK - inset;
  const s = new Shape();
  s.moveTo(f + 0.05, -hw);
  s.lineTo(b - 0.06, -hw);
  s.quadraticCurveTo(b, -hw, b, -hw + 0.06);
  s.lineTo(b, hw - 0.06);
  s.quadraticCurveTo(b, hw, b - 0.06, hw);
  s.lineTo(f + 0.05, hw);
  s.quadraticCurveTo(f, hw, f, hw - 0.05);
  s.lineTo(f, -hw + 0.05);
  s.quadraticCurveTo(f, -hw, f + 0.05, -hw);
  return s;
}

/** The plan's axes to the scooter's: the shape's x along the deck (z), its y across (x), the extrusion up (y). */
const PLAN_UP = new Matrix4().set(0, 1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 0, 1);

/** A plan shape extruded from `y0` up to `y1`: the deck and what lies on it. */
function plate(shape: Shape, y0: number, y1: number): BufferGeometry {
  const g = new ExtrudeGeometry(shape, { depth: y1 - y0, bevelEnabled: false, curveSegments: 4 });
  return g.applyMatrix4(PLAN_UP).translate(0, y0, 0);
}

/**
 * The parked scooter: the ridden one's outline — wheels, deck, stem, bars,
 * fenders — in a few hundred triangles. Its trim is `scooterPaint`, for the
 * instance colour to tint.
 */
export function createParkedScooter(): Group {
  const kit = new Kit();
  for (const z of [SCOOTER.frontAxleZ, SCOOTER.rearAxleZ]) {
    const r = SCOOTER.frontRadius, w = SCOOTER.frontTyre;
    kit.add(rubber, new CylinderGeometry(r, r, w, 10, 1, true).rotateZ(Math.PI / 2).translate(0, r, z));
    for (const s of [-1, 1]) {
      kit.add(rubber, new CircleGeometry(r, 10).rotateY(s * Math.PI / 2).translate(s * w / 2, r, z));
      kit.add(frameBlack, new CircleGeometry(r * 0.55, 10).rotateY(s * Math.PI / 2).translate(s * (w / 2 + 0.003), r, z));
    }
  }
  kit.add(frameBlack, plate(deckShape(), DECK_TOP - 0.06, DECK_TOP - 0.004));
  kit.add(gripTape, plate(deckShape(0.015), DECK_TOP - 0.004, DECK_TOP));
  for (const s of [-1, 1]) kit.add(scooterPaint, new BoxGeometry(0.006, 0.012, DECK_BACK - DECK_FRONT - 0.12).translate(s * (DECK_WIDTH / 2 + 0.001), DECK_TOP - 0.03, (DECK_FRONT + DECK_BACK) / 2));
  // The neck up to the hinge, the stem, the bars.
  kit.add(frameBlack, tube([[0, DECK_TOP - 0.03, DECK_FRONT + 0.02], [0, 0.26, -0.36], [0, 0.34, -0.41]], 0.028, 6));
  const axis = new Vector3(0, Math.cos(RAKE), Math.sin(RAKE));
  const at = (along: number): V3 => [0, HEAD.y + axis.y * along, HEAD.z + axis.z * along];
  kit.add(frameBlack, rod(at(-0.68), at(0.17), 0.024, 8));
  const top = at(0.19);
  kit.add(frameBlack, beam([-0.3, top[1], top[2]], [0.3, top[1], top[2]], 0.024, 0.024));
  // Fork legs, the front fender, the rear fender.
  for (const s of [-1, 1]) kit.add(scooterPaint, beam([s * 0.07, 0.42, -0.42], [s * 0.07, SCOOTER.frontRadius, SCOOTER.frontAxleZ], 0.03, 0.04));
  kit.add(frameBlack, placed(new BoxGeometry(0.1, 0.02, 0.2), 0, 2 * SCOOTER.frontRadius + 0.03, SCOOTER.frontAxleZ, 0.1));
  kit.add(frameBlack, placed(new BoxGeometry(0.1, 0.02, 0.26), 0, 2 * SCOOTER.rearRadius + 0.03, SCOOTER.rearAxleZ + 0.02, -0.35));
  kit.add(scooterTaillamp, new BoxGeometry(0.06, 0.014, 0.02).translate(0, 2 * SCOOTER.rearRadius + 0.04, SCOOTER.rearAxleZ + 0.14));
  return kit.into(new Group());
}

// --- the model --------------------------------------------------------------------

export interface ScooterModel {
  kind: 'scooter';
  root: Group;
  /** The stem and bars, turning about the raked steering axis. */
  steer: Group;
  /** The fork's sliding half with the front wheel: moves along the stem. */
  forkLower: Group;
  frontWheel: Group;
  rearWheel: Group;
  swingarm: Group;
  /** The rear shocks: one each side, their rods and springs. */
  shocks: Group[];
  shockRods: Group[];
  shockSprings: Mesh[];
  /** The fork springs, stretched between the crown and the sliders. */
  forkSprings: Mesh[];
  headlight: SpotLight;
  /** A side stand, down when parked: folds up under the deck when ridden. */
  stand: Group;
  /** Its own lit parts: the display's glass and the deck's LED strips. */
  screen: MeshStandardMaterial;
  leds: MeshStandardMaterial;
}

/** A coil of `turns` over a unit length, radius `r`, to be scaled to its gap. */
function coil(turns: number, r: number, thickness: number): Mesh {
  const pts: V3[] = [];
  for (let i = 0; i <= turns * 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    pts.push([r * Math.cos(a), -i / (turns * 10), r * Math.sin(a)]);
  }
  const mesh = new Mesh(new TubeGeometry(new CatmullRomCurve3(pts.map(vec)), turns * 20, thickness, 5, false), scooterPaint);
  mesh.castShadow = true;
  return mesh;
}

/**
 * Build the scooter. The root's origin is on the ground midway between the
 * axles; its parts sit at static sag and are moved by `animateScooter`.
 */
export function createScooter(): ScooterModel {
  const root = new Group();
  const chassis = new Kit();
  const display = makeDisplay(), deckLeds = makeDeckLeds();

  // --- the deck: the battery box, grip tape, LED strips, the neck and the tail --
  chassis.add(frameBlack, plate(deckShape(), DECK_TOP - 0.06, DECK_TOP - 0.004));
  chassis.add(gripTape, plate(deckShape(0.015), DECK_TOP - 0.004, DECK_TOP));
  // The battery's cover under it, and a charging port flap.
  chassis.add(black, plate(deckShape(0.02), DECK_TOP - 0.075, DECK_TOP - 0.06));
  chassis.add(darkAlu, new BoxGeometry(0.03, 0.012, 0.04).translate(-DECK_WIDTH / 2 + 0.005, DECK_TOP - 0.035, 0.1));
  const ledLength = DECK_BACK - DECK_FRONT - 0.14;
  for (const s of [-1, 1]) {
    chassis.add(deckLeds, new BoxGeometry(0.006, 0.01, ledLength).translate(s * (DECK_WIDTH / 2 + 0.002), DECK_TOP - 0.028, (DECK_FRONT + DECK_BACK) / 2));
    chassis.add(scooterPaint, new BoxGeometry(0.006, 0.006, ledLength).translate(s * (DECK_WIDTH / 2 + 0.002), DECK_TOP - 0.016, (DECK_FRONT + DECK_BACK) / 2));
  }
  // The deck lamp at the front edge.
  chassis.add(scooterHeadlamp, new BoxGeometry(0.06, 0.012, 0.01).translate(0, DECK_TOP - 0.03, DECK_FRONT - 0.002));
  // The neck: a cast arm from the deck's nose up to the folding hinge.
  chassis.add(frameBlack, tube([[0, DECK_TOP - 0.035, DECK_FRONT + 0.04], [0, 0.2, -0.33], [0, 0.29, -0.385], [0, 0.345, -0.405]], 0.03, 8));
  chassis.add(frameBlack, new BoxGeometry(0.075, 0.06, 0.07).translate(0, 0.3, -0.37));
  // The head tube the stem turns in, and the hinge's latch.
  const axis = new Vector3(0, Math.cos(RAKE), Math.sin(RAKE));
  const at = (along: number): V3 => [HEAD.x + axis.x * along, HEAD.y + axis.y * along, HEAD.z + axis.z * along];
  chassis.add(darkAlu, rod(at(-0.72), at(-0.6), 0.03, 10));
  chassis.add(scooterPaint, beam([0.04, 0.3, -0.33], [0.04, 0.38, -0.37], 0.012, 0.03));
  // The tail: the swingarm's mounts and the shock towers.
  for (const s of [-1, 1]) {
    chassis.add(frameBlack, new BoxGeometry(0.02, 0.09, 0.1).translate(s * 0.065, 0.19, 0.3));
    chassis.add(frameBlack, beam([s * 0.07, DECK_TOP - 0.03, 0.3], [s * SHOCK_TOP.x, SHOCK_TOP.y, SHOCK_TOP.z], 0.02, 0.035));
  }
  chassis.add(chrome, new CylinderGeometry(0.012, 0.012, 0.16, 8).rotateZ(Math.PI / 2).translate(PIVOT.x, PIVOT.y, PIVOT.z));
  chassis.into(root);

  // --- the side stand, on the left, down when parked --------------------------
  const stand = new Group();
  stand.position.set(-0.085, DECK_TOP - 0.07, 0.12);
  const standKit = new Kit();
  standKit.add(darkAlu, rod([0, 0, 0], [-0.1, -0.09, 0.02], 0.007, 6), new BoxGeometry(0.03, 0.008, 0.03).translate(-0.1, -0.095, 0.02));
  standKit.into(stand);
  root.add(stand);

  // --- the steering: the stem, tilted along the rake --------------------------
  const tilt = new Group();
  tilt.position.copy(HEAD);
  tilt.rotation.x = RAKE;
  const steer = new Group();
  tilt.add(steer);
  root.add(tilt);
  const bars = new Kit();
  // The stem: a stout tube from the crown to the clamp, the hinge above the crown.
  bars.add(frameBlack, rod([0, -0.6, 0], [0, 0.14, 0], 0.024, 10));
  bars.add(darkAlu, new BoxGeometry(0.07, 0.07, 0.06).translate(0, -0.66, 0.0));
  bars.add(frameBlack, new BoxGeometry(0.06, 0.06, 0.055).translate(0, -0.56, 0.0));
  bars.add(scooterPaint, new BoxGeometry(0.062, 0.012, 0.057).translate(0, -0.6, 0));
  // The crown and the fork's upper legs, with their springs' housings.
  bars.add(darkAlu, new BoxGeometry(0.19, 0.045, 0.06).translate(0, -0.69, 0));
  for (const s of [-1, 1]) {
    bars.add(frameBlack, rod([s * 0.07, -0.71, 0], [s * 0.07, -0.78, 0], 0.022, 8));
    bars.add(darkAlu, new CylinderGeometry(0.03, 0.03, 0.014, 8).translate(s * 0.07, -0.78, 0));
  }
  // The bars: a straight bar with risers, grips, levers, a thumb throttle and the bell.
  bars.add(frameBlack, new BoxGeometry(0.06, 0.03, 0.05).translate(0, 0.155, -0.01));
  bars.add(frameBlack, rod([-0.31, GRIP.y, GRIP.z], [0.31, GRIP.y, GRIP.z], 0.011, 8));
  for (const s of [-1, 1] as const) {
    bars.add(rubber, rod([s * 0.2, GRIP.y, GRIP.z], [s * 0.315, GRIP.y, GRIP.z], 0.017, 8));
    for (let i = 0; i < 4; i++) bars.add(black, rod([s * (0.21 + i * 0.024), GRIP.y, GRIP.z], [s * (0.216 + i * 0.024), GRIP.y, GRIP.z], 0.0185, 8));
    bars.add(black, new BoxGeometry(0.04, 0.03, 0.035).translate(s * 0.17, GRIP.y, GRIP.z - 0.012));
    bars.add(alu, beam([s * 0.17, GRIP.y - 0.005, GRIP.z - 0.025], [s * 0.3, GRIP.y - 0.015, GRIP.z - 0.085], 0.01, 0.007));
  }
  bars.add(scooterPaint, new BoxGeometry(0.035, 0.028, 0.03).translate(0.15, GRIP.y + 0.02, GRIP.z + 0.015));
  bars.add(chrome, new CylinderGeometry(0.018, 0.018, 0.012, 10).rotateX(Math.PI / 2).translate(-0.14, GRIP.y + 0.025, GRIP.z + 0.01));
  // The round display in the middle of the bars, and the headlamp on the stem.
  bars.add(darkAlu, new CylinderGeometry(0.04, 0.04, 0.02, 14).rotateX(Math.PI / 2 - 0.5).translate(0, GRIP.y + 0.02, GRIP.z + 0.02));
  bars.add(display, new CylinderGeometry(0.033, 0.033, 0.004, 14).rotateX(Math.PI / 2 - 0.5).translate(0, GRIP.y + 0.0275, GRIP.z + 0.006));
  bars.add(frameBlack, new BoxGeometry(0.06, 0.05, 0.05).translate(0, -0.1, -0.025));
  bars.add(scooterHeadlamp, new BoxGeometry(0.045, 0.03, 0.008).translate(0, -0.1, -0.052));
  bars.into(steer);

  const headlight = new SpotLight(0xfff0d0, 0, 45, 0.5, 0.6, 1.4);
  headlight.position.set(0, -0.1, -0.06);
  headlight.target.position.set(0, -8, -14);
  steer.add(headlight, headlight.target);

  // --- the fork's lower half: sliders, guards in the trim colour, caliper, wheel ---
  const forkLower = new Group();
  forkLower.position.y = -FORK_AT_SAG;
  steer.add(forkLower);
  const lower = new Kit();
  for (const s of [-1, 1] as const) {
    lower.add(chrome, rod([s * 0.07, 0.03, 0], [s * 0.07, 0.13, 0], 0.014, 8));
    lower.add(scooterPaint, new BoxGeometry(0.036, 0.06, 0.04).translate(s * 0.07, 0, 0));
  }
  lower.add(darkAlu, new BoxGeometry(0.16, 0.03, 0.03).translate(0, 0.0, 0.0));
  lower.into(forkLower);
  const lowerUpright = new Group();
  lowerUpright.rotation.x = -RAKE;
  forkLower.add(lowerUpright);
  const caliper = new Kit();
  caliper.add(darkAlu, new BoxGeometry(0.03, 0.05, 0.04).translate(-0.05, 0.045, 0.05));
  // The front fender, over the wheel.
  const fenderArc = (radius: number, a0: number, a1: number, n: number) =>
    Array.from({ length: n + 1 }, (_, i): [number, number] => {
      const a = a0 + ((a1 - a0) * i) / n;
      return [-radius * Math.sin(a), radius * Math.cos(a)];
    });
  caliper.add(frameBlack, slab([...fenderArc(0.165, -0.75, 0.85, 7), ...fenderArc(0.152, 0.85, -0.75, 7)], 0.09));
  caliper.into(lowerUpright);
  const frontWheel = buildWheel(SCOOTER.frontRadius, SCOOTER.frontTyre, false);
  lowerUpright.add(frontWheel);
  // The fork springs, in the trim colour, between the crown's housings and the sliders.
  const forkSprings: Mesh[] = [];
  for (const s of [-1, 1]) {
    const spring = coil(7, 0.02, 0.0045);
    spring.position.set(s * 0.07, -0.78, 0);
    steer.add(spring);
    forkSprings.push(spring);
  }

  // --- the swingarm, the rear fender, the hub-motor wheel ------------------------
  const swingarm = new Group();
  swingarm.position.copy(PIVOT);
  root.add(swingarm);
  const arm = new Kit();
  for (const s of [-1, 1] as const) {
    arm.add(frameBlack, beam([s * 0.055, 0, 0], [s * 0.062, ARM.y, ARM.z], 0.022, 0.04));
    arm.add(scooterPaint, new BoxGeometry(0.024, 0.03, 0.05).translate(s * 0.062, ARM.y, ARM.z - 0.03));
    arm.add(darkAlu, new BoxGeometry(0.03, 0.03, 0.03).translate(s * SHOCK_FOOT.x, SHOCK_FOOT.y, SHOCK_FOOT.z));
  }
  arm.add(frameBlack, new CylinderGeometry(0.02, 0.02, 0.12, 8).rotateZ(Math.PI / 2), new BoxGeometry(0.11, 0.03, 0.05).translate(0, -0.01, 0.07));
  // The rear fender, on the arm over the wheel, carrying the brake lamp and the plate holder.
  const rearArc = (radius: number, a0: number, a1: number, n: number) =>
    Array.from({ length: n + 1 }, (_, i): [number, number] => {
      const a = a0 + ((a1 - a0) * i) / n;
      return [ARM.z - radius * Math.sin(a), ARM.y + radius * Math.cos(a)];
    });
  arm.add(frameBlack, slab([...rearArc(0.17, -1.3, 0.55, 8), ...rearArc(0.157, 0.55, -1.3, 8)], 0.09));
  arm.add(scooterTaillamp, new BoxGeometry(0.06, 0.016, 0.02).translate(0, ARM.y + 0.165, ARM.z + 0.055));
  arm.add(darkAlu, new BoxGeometry(0.03, 0.05, 0.04).translate(-0.05, ARM.y + 0.04, ARM.z - 0.05));
  arm.into(swingarm);
  const rearWheel = buildWheel(SCOOTER.rearRadius, SCOOTER.rearTyre, true);
  rearWheel.position.copy(ARM);
  swingarm.add(rearWheel);
  // The motor's cable along the arm.
  const cable = new Kit();
  cable.add(black, tube([[-0.07, 0.01, 0.0], [-0.075, ARM.y + 0.04, ARM.z * 0.5], [-0.075, ARM.y + 0.01, ARM.z - 0.03]], 0.005, 5));
  cable.into(swingarm);

  // --- the rear shocks: one each side, body from the top, rod from the foot, spring --
  const shocks: Group[] = [], shockRods: Group[] = [], shockSprings: Mesh[] = [];
  for (const s of [-1, 1]) {
    const shock = new Group();
    shock.position.set(s * SHOCK_TOP.x, SHOCK_TOP.y, SHOCK_TOP.z);
    root.add(shock);
    const body = new Kit();
    body.add(darkAlu, rod([0, 0.01, 0], [0, -0.09, 0], 0.016, 8), new CylinderGeometry(0.024, 0.024, 0.008, 8).translate(0, -0.02, 0));
    body.into(shock);
    const shockRod = new Group();
    shock.add(shockRod);
    const rodKit = new Kit();
    rodKit.add(chrome, rod([0, 0, 0], [0, 0.12, 0], 0.005, 6));
    rodKit.add(darkAlu, new BoxGeometry(0.02, 0.03, 0.02).translate(0, 0.005, 0), new CylinderGeometry(0.024, 0.024, 0.008, 8).translate(0, 0.03, 0));
    rodKit.into(shockRod);
    const spring = coil(8, 0.021, 0.004);
    spring.position.y = -0.024;
    shock.add(spring);
    shocks.push(shock);
    shockRods.push(shockRod);
    shockSprings.push(spring);
  }

  return { kind: 'scooter', root, steer, forkLower, frontWheel, rearWheel, swingarm, shocks, shockRods, shockSprings, forkSprings, headlight, stand, screen: display, leds: deckLeds };
}

/** What the model needs each frame to move like a scooter. */
export interface ScooterMotion {
  speed: number;
  steerAngle: number;
  /** Compression, metres, from `updateMotoRide`. */
  front: number;
  rear: number;
  braking: boolean;
  /** 0 day … 1 night. */
  night: number;
  /** Being ridden: the stand up, the display lit. */
  ridden: boolean;
}

const shockFoot = new Vector3();
const shockDir = new Vector3();

/**
 * Move the scooter's parts for a frame: wheels rolling, the stem turned,
 * the fork and the swingarm to their compression with the springs squeezed
 * between their mounts, the lamps and the deck LEDs lit for the night.
 */
export function animateScooter(model: ScooterModel, motion: ScooterMotion, dt: number): void {
  const rate = motion.speed / SCOOTER.frontRadius;
  model.frontWheel.rotation.x -= rate * dt;
  model.rearWheel.rotation.x -= rate * dt;

  model.steer.rotation.y = -motion.steerAngle;
  model.forkLower.position.y = -(FORK_AT_SAG + SCOOTER.frontSag - motion.front);
  // The fork springs from the housings at −0.78 down to the sliders' tops.
  const forkGap = Math.max(0.02, -0.78 - (model.forkLower.position.y + 0.03));
  for (const spring of model.forkSprings) spring.scale.y = forkGap;

  const drop = -ARM.y + (SCOOTER.rearSag - motion.rear);
  model.swingarm.rotation.x = Math.asin(Math.max(-1, Math.min(1, drop / ARM_LENGTH))) - Math.asin(-ARM.y / ARM_LENGTH);
  model.shocks.forEach((shock, i) => {
    const s = i === 0 ? -1 : 1;
    shockFoot.set(s * SHOCK_FOOT.x, SHOCK_FOOT.y, SHOCK_FOOT.z).applyAxisAngle(X_AXIS, model.swingarm.rotation.x).add(PIVOT);
    shockDir.copy(shockFoot).sub(v4.set(s * SHOCK_TOP.x, SHOCK_TOP.y, SHOCK_TOP.z));
    const length = shockDir.length();
    shock.quaternion.setFromUnitVectors(DOWN, shockDir.divideScalar(length));
    model.shockRods[i].position.y = -length;
    model.shockSprings[i].scale.y = Math.max(0.03, length - 0.06);
  });

  // The stand swings up and back under the deck when ridden, and comes down when parked.
  const standUp = motion.ridden ? -1.6 : 0;
  model.stand.rotation.x += (standUp - model.stand.rotation.x) * (1 - Math.exp(-dt * 8));

  if (motion.ridden) {
    scooterHeadlamp.emissiveIntensity = 0.35 + 2.4 * motion.night;
    scooterTaillamp.emissiveIntensity = motion.braking ? 2.6 : 0.4 + 1.2 * motion.night;
    model.leds.emissive.copy(scooterPaint.color);
  }
  model.leds.emissiveIntensity = motion.ridden ? 0.3 + 1.8 * motion.night : 0;
  model.screen.emissiveIntensity = motion.ridden ? 0.9 : 0;
  model.headlight.intensity = motion.ridden ? 14 * motion.night : 0;
}

// --- the rider ----------------------------------------------------------------

/** Where the feet stand on the deck: the left ahead, the right behind, in line down its middle. */
const FOOT_LEFT = new Vector3(-0.035, DECK_TOP + 0.07, -0.1);
const FOOT_RIGHT = new Vector3(0.04, DECK_TOP + 0.07, 0.17);

/** Where a grip is, in the scooter's frame, with the bars turned `steer` (radians, right positive). */
export function scooterGripPoint(side: 1 | -1, steer: number, out = new Vector3()): Vector3 {
  out.set(side * GRIP.x, GRIP.y, GRIP.z);
  out.applyAxisAngle(Y_AXIS, -steer);
  out.applyAxisAngle(X_AXIS, RAKE);
  return out.add(HEAD);
}

/** A rider's state to stand on the scooter: feet on the deck, not on pegs. */
export function createScooterRiderState(): RiderState {
  const v = () => new Vector3();
  return {
    stand: 1, footOut: 0, footDown: 1, wheelie: 0, bob: 0, bobV: 0,
    pose: {
      hips: { x: 0, y: 1.02, z: 0.06, pitch: 0, roll: 0, yaw: 0 },
      torso: { pitch: 0, roll: 0, twist: 0 },
      head: { pitch: 0, yaw: 0 },
      hands: [v(), v()], elbows: [v(), v()], feet: [v(), v()], knees: [v(), v()], toes: [0, 0],
      helmet: false,
    },
  };
}

/**
 * The rider standing on the deck for this frame, in the scooter's frame:
 * upright at a walk, crouched a little over the bars at speed and when
 * braking, leaning with the machine in a turn, knees soaking up a bump.
 * Stopped, the left foot goes down to the ground and the scooter leans on
 * it a touch, which is returned for the renderer to add.
 */
export function scooterRiderPose(s: RiderState, m: RiderMotion, dt: number): { pose: RiderPose; bikeLean: number } {
  const v = Math.abs(m.speed);
  const ease = (rate: number) => 1 - Math.exp(-dt * rate);
  // `stand` is the crouch here: 0 upright … 1 tucked down at speed.
  const wantCrouch = m.airborne ? 0.6 : smoothstep(5, 12, v) * 0.7 + (m.accel < -2 ? 0.25 : 0);
  const wantDown = !m.airborne && v < 0.5 ? 1 : 0;
  s.stand += (wantCrouch - s.stand) * ease(4);
  s.footDown += (wantDown - s.footDown) * ease(4);
  if (m.landing < -1) s.bobV += Math.max(-1.2, m.landing * 0.12);
  s.bobV += (-160 * s.bob - 14 * s.bobV) * dt;
  s.bob = Math.max(-0.14, Math.min(0.05, s.bob + s.bobV * dt));

  const p = s.pose;
  const crouch = s.stand;
  // Hips over the deck, dropped and brought forward by the crouch, dipped by a bump.
  p.hips.x = 0;
  p.hips.y = 1.02 - 0.1 * crouch + s.bob + Math.max(-0.03, Math.min(0.02, -m.accel * 0.004));
  p.hips.z = 0.07 - 0.04 * crouch + Math.max(-0.03, Math.min(0.04, m.accel * 0.006));
  p.hips.pitch = 0.08 + 0.25 * crouch;
  // Leaning with the machine, a little less than it does.
  p.hips.roll = -m.lean * 0.25;
  p.hips.yaw = 0.12;
  p.torso.pitch = 0.1 + 0.15 * crouch - s.bob * 1.1;
  p.torso.roll = 0;
  p.torso.twist = m.steerAngle * 0.25;
  p.head.pitch = -(p.hips.pitch + p.torso.pitch) + 0.05;
  p.head.yaw = m.steerAngle * 0.5 + m.lean * 0.3;

  for (const i of [0, 1] as const) {
    const side = i === 0 ? -1 : 1;
    scooterGripPoint(side, m.steerAngle, p.hands[i]);
    // Elbows out and down, more out as the body comes down over the bars.
    p.elbows[i].set(side * 0.7, -0.5 + 0.3 * crouch, 0.4);
    const foot = i === 0 ? FOOT_LEFT : FOOT_RIGHT;
    p.feet[i].copy(foot);
    // Knees ahead and a touch out; the back knee bends more in the crouch.
    p.knees[i].set(side * 0.15, 0.1 + 0.3 * crouch * (i === 1 ? 1 : 0.6), -1);
    p.toes[i] = 0;
    if (i === 0 && s.footDown > 0.01) {
      p.feet[i].lerp(v4.set(-0.3, 0.07, -0.02), s.footDown);
      p.knees[i].lerp(v4.set(-0.5, 0.2, -0.8), s.footDown);
    }
  }
  return { pose: p, bikeLean: -0.08 * s.footDown };
}
